import type { Action, Tournament } from '../types.js';
import { EngineError, createTournament } from './state.js';
import { clone } from '../clone.js';
import { formFehler } from './form.js';
import { apply } from './reduce/apply.js';

export { clubKey } from './keys.js';
// Die Aktionsfamilien liegen unter ./reduce/ — hier bleibt nur reduce/replay
export { ALGORITHM_VERSION, qualifiersFromGroups } from './reduce/phases.js';
export { requiredCourtGroup } from './reduce/matches.js';

export type ReduceResult = { ok: true; state: Tournament; warnings: string[] } | { ok: false; state: Tournament; error: string; warnings: string[] };

/**
 * Reine Zustandsfunktion (docs/04 §9, docs/12 §3). Der Eingabezustand wird nie
 * verändert. Jede erfolgreiche Aktion landet im Log; `undo` spielt das Log ohne
 * die letzte Aktion neu ab.
 */
export function reduce(state: Tournament, action: Action): ReduceResult {
  const form = formFehler(action);
  if (form) return { ok: false, state, error: form, warnings: [] };
  if (action.type === 'undo') {
    if (state.log.length === 0) return { ok: false, state, error: 'Nichts rückgängig zu machen', warnings: [] };
    return { ok: true, state: replay(state.init, state.log.slice(0, -1)), warnings: [] };
  }
  if (action.type === 'revert') {
    const last = lastEffective(state.log);
    if (!last) return { ok: false, state, error: 'Nichts rückgängig zu machen', warnings: [] };
    if (!last.id || last.id !== action.targetId) return { ok: false, state, error: 'Nur die letzte Aktion lässt sich widerrufen', warnings: [] };
    try {
      return { ok: true, state: replay(state.init, [...state.log, action]), warnings: [] };
    } catch (e) {
      return { ok: false, state, ...fehlerAus(e) };
    }
  }
  const next = kopieOhneLog(state);
  try {
    const warnings = apply(next, action);
    next.log.push(action);
    return { ok: true, state: next, warnings };
  } catch (e) {
    return { ok: false, state, ...fehlerAus(e) };
  }
}

/**
 * Jeder Fehler aus `apply` ist eine abgelehnte Aktion — nicht nur
 * `EngineError`. Ein TypeError aus einer beschädigten Aktion darf weder die
 * Oberfläche noch den Abgleich umwerfen (siehe `formFehler`).
 */
function fehlerAus(e: unknown): { error: string; warnings: string[] } {
  if (e instanceof EngineError) return { error: e.message, warnings: e.warnings };
  return { error: `Aktion nicht anwendbar: ${e instanceof Error ? e.message : String(e)}`, warnings: [] };
}

/**
 * Tiefe Kopie des Zustands **ohne** das Protokoll. Das Protokoll wächst mit
 * jeder Eingabe und war der größte Teil jeder Kopie; seine Einträge ändert
 * `apply` nie, es wird nur angehängt. Deshalb genügt eine flache Kopie des
 * Arrays — `push` auf der Kopie lässt den alten Zustand unberührt.
 */
function kopieOhneLog(state: Tournament): Tournament {
  return kopieMit(state, state.log.slice());
}

/**
 * Tiefe Kopie aller Felder außer `log`, das durch `log` ersetzt wird. Die
 * Reihenfolge der Felder bleibt, wie sie war — `JSON.stringify` zweier Stände
 * vergleichen das Backup und die Sync-Tests, und dort zählt sie.
 */
function kopieMit(state: Tournament, log: Action[]): Tournament {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(state)) {
    if (k === 'log') out[k] = log;
    else if (v !== undefined) out[k] = clone(v);
  }
  return out as unknown as Tournament;
}

/** Letzte Aktion, die noch wirkt (kein Widerruf, nicht widerrufen) */
export function lastEffective(log: readonly Action[]): Action | undefined {
  const reverted = revertedIds(log);
  for (let i = log.length - 1; i >= 0; i--) {
    const a = log[i] as Action;
    if (a.type === 'undo' || a.type === 'revert') continue;
    if (a.id && reverted.has(a.id)) continue;
    return a;
  }
  return undefined;
}

function revertedIds(actions: readonly Action[]): Set<string> {
  const out = new Set<string>();
  for (const a of actions) if (a && a.type === 'revert' && typeof a.targetId === 'string') out.add(a.targetId);
  return out;
}

export function replay(init: Tournament['init'], actions: readonly Action[]): Tournament {
  const t = createTournament(init);
  const reverted = revertedIds(actions);
  for (const a of actions) {
    const form = formFehler(a);
    if (form) throw new EngineError(form);
    if (a.type === 'undo') continue;
    if (a.type === 'revert' || (a.id && reverted.has(a.id))) {
      t.log.push(a);
      continue;
    }
    apply(t, a);
    t.log.push(a);
  }
  return t;
}

export interface Rejected {
  action: Action;
  error: string;
}

/**
 * Replay, das fehlschlagende Aktionen überspringt statt abzubrechen — für das
 * Zusammenführen von Protokollen mehrerer Geräte (docs/14): Wenn zwei Geräte
 * offline dasselbe Feld belegt haben, gewinnt die Aktion, die zuerst beim
 * Server war; die zweite wird verworfen und gemeldet.
 */
export function replayLenient(init: Tournament['init'], actions: readonly Action[]): { state: Tournament; rejected: Rejected[] } {
  /*
   * Schnell und ohne Kopien: Früher wurde vor **jeder** Aktion der ganze
   * Zustand kopiert, um sie bei einem Fehler zurückdrehen zu können — bei 1200
   * Aktionen 2,2 s statt 26 ms, und das bei jedem Abgleich. Jetzt gibt es nur
   * alle `STUETZ` Aktionen einen Stützpunkt (Kopie ohne Protokoll). Scheitert
   * eine Aktion, geht es vom letzten Stützpunkt aus weiter: Kopie holen, die
   * seitdem angenommenen Aktionen noch einmal anwenden — `apply` ist
   * deterministisch, das Ergebnis also genau der Stand vor der gescheiterten
   * Aktion. Ohne Fehler kostet das Ganze kaum mehr als ein strenges Replay.
   */
  let t = createTournament(init);
  const rejected: Rejected[] = [];
  const reverted = revertedIds(actions);
  let stuetz = stuetzpunkt(t);
  /** seit dem Stützpunkt: angenommene Aktionen (`true` = nur ins Protokoll, nicht anwenden) */
  let seither: [Action, boolean][] = [];
  for (const a of actions) {
    const form = formFehler(a);
    if (form) {
      rejected.push({ action: a, error: form });
      continue;
    }
    if (a.type === 'undo') continue;
    if (a.type === 'revert' || (a.id && reverted.has(a.id))) {
      t.log.push(a);
      seither.push([a, true]);
    } else {
      try {
        apply(t, a);
        t.log.push(a);
        seither.push([a, false]);
      } catch (e) {
        // `apply` kann schon teilweise verändert haben → Stand vor der Aktion neu aufbauen
        rejected.push({ action: a, error: fehlerAus(e).error });
        t = zurueck(stuetz, seither);
      }
    }
    if (seither.length >= STUETZ) {
      stuetz = stuetzpunkt(t);
      seither = [];
    }
  }
  return { state: t, rejected };
}

/** Abstand der Stützpunkte in `replayLenient` */
const STUETZ = 64;

/** Stützpunkt: tiefe Kopie ohne Protokoll, dazu eine flache Kopie des Protokolls (es wird nur angehängt) */
type Stuetzpunkt = Tournament;
function stuetzpunkt(t: Tournament): Stuetzpunkt {
  return kopieOhneLog(t);
}
function zurueck(s: Stuetzpunkt, seither: readonly [Action, boolean][]): Tournament {
  const t = kopieOhneLog(s);
  for (const [a, nurProtokoll] of seither) {
    if (!nurProtokoll) apply(t, a);
    t.log.push(a);
  }
  return t;
}
